import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CanalCampana, ContenidoCampana, Disparador, EnvioAutomatico, PlantillaMarketing, Regla, Resultados, ResumenResultados, Segmento, VersionRegla, VistaPreviaRegla } from '../../api/ops/campanas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Casilla } from '../../componentes/Casilla';
import { Chip } from '../../componentes/Chip';
import { Contexto } from '../../componentes/Contexto';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { cifra, diaMes, diaYHora, importe } from '../../logica/fechasCortas';
import { usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsApartado } from './Campanas';
import { HORAS, paraQuienDe, segmentoDeRegla, textoParaQuien, variablesDe, type ParaQuien, type Variable } from './editor';
import { accionesRegla, AYUDA, SUBTITULO, CANAL, cuandoSale, delAl, DISPARADOR, ESTADO_ENVIO_AUTOMATICO, ESTADO_REGLA, horaPorDefecto, motivoEnvio, motivosDe, OMITIDO, PAUSA_REGLA, periodoAnterior, periodos, porcentaje, RUTA_PLANTILLAS_META, variacion, type AccionRegla, type Periodo } from './reglas';
import { Cifra } from './Resumen';

/**
 * 12.5 Mensajes automáticos: Mensajes · Envíos · Archivados; aviso de los pausados (plantilla G762: «Se activa sola cuando
 * Meta la vuelva a aprobar», canal G744); ficha Mensaje · Resultados · Envíos · Versiones; crear (borrador), cambiar
 * (versión nueva), activar, pausar y archivar (también un borrador, G742, con ventana). La hora es opcional en «Al día
 * siguiente» y «Cumpleaños» («Elegir la hora», G740, G748). Al guardar se calcula la vista previa (`previewAutomationRule`;
 * en el cumpleaños, `with_birth_date` si llega, G802). El detalle por cliente (Envíos) solo con `campanas.destinatarios.ver`.
 * Arriba, la frase del periodo y, al pedirlo, «Resultados» con cuatro cifras y su chip frente al periodo anterior de la
 * misma duración (tarea 35; G828, G892): solo los automáticos, de `automation_totals` frente a `previous_automation_totals`
 * (G993; el panel no resta las campañas del total, G121). El periodo es contexto: en ordenador, en la línea del título.
 */
type Vista = 'reglas' | 'envios' | 'archivados';

export function Automaticos(props: PropsApartado) {
    const { ops, negocio, movil, soloLectura, busqueda, abrirAjustes } = props;
    const { puede } = usePermisos();
    const gestiona = puede('campanas.automaticos.gestionar') && !soloLectura;
    const veDetalle = puede('campanas.destinatarios.ver');
    const vistas: { valor: Vista; etiqueta: string }[] = [{ valor: 'reglas', etiqueta: 'Mensajes' }, ...(veDetalle ? [{ valor: 'envios' as const, etiqueta: 'Envíos' }] : []), { valor: 'archivados', etiqueta: 'Archivados' }];
    const [vista, setVista] = useState<Vista>('reglas');
    const [reglas, setReglas] = useState<Regla[] | null>(null);
    const [franja, setFranja] = useState<{ from: string } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [abierta, setAbierta] = useState<string | null>(busqueda.get('regla'));
    const [editar, setEditar] = useState<Regla | 'nueva' | null>(null);
    const [hecho, setHecho] = useState<string | null>(null);
    const veResumen = puede('campanas.resultados.ver_resumen');
    const listaPeriodos = useMemo(() => periodos(), []);
    const [valorPeriodo, setValorPeriodo] = useState<Periodo['valor']>('30d');
    const periodo = listaPeriodos.find((p) => p.valor === valorPeriodo) ?? listaPeriodos[0];
    const [verResultados, setVerResultados] = useState(false);
    const [resumen, setResumen] = useState<ResumenResultados | null>(null);
    const [atribucion, setAtribucion] = useState<number | null>(null);
    const selectorPeriodo = {
        clave: 'periodo',
        etiqueta: 'Periodo',
        valor: valorPeriodo,
        opciones: listaPeriodos.map((p) => ({ valor: p.valor, texto: p.etiqueta })),
        icono: 'calendar' as const,
        onChange: (v: string) => {
            setResumen(null);
            setValorPeriodo(v as Periodo['valor']);
        },
    };

    useCabecera({
        ayuda: AYUDA.automaticos,
        subtitulo: SUBTITULO.equipo,
        contexto: !movil && verResultados && veResumen ? [selectorPeriodo] : [],
        acciones: [
            ...(!movil ? [{ clave: 'ajustes', texto: 'Ajustes', icono: 'sliders-horizontal' as const, secundaria: true, onClick: abrirAjustes }] : []),
            ...(gestiona ? [{ clave: 'nuevo', texto: 'Nuevo mensaje', icono: 'plus' as const, onClick: () => { setHecho(null); setEditar('nueva'); } }] : []),
        ],
    });

    useEffect(() => {
        let vivo = true;
        ops.listAutomationRules(negocio, { limit: 100 })
            .then((r) => {
                if (vivo) {
                    setReglas(r.data);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        ops.getCampaignSettings(negocio)
            .then((r) => {
                if (vivo) {
                    setFranja(r.datos.automatic_send_window);
                    setAtribucion(r.datos.attribution_window_days);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento]);

    useEffect(() => {
        if (!veResumen || periodo === undefined) {
            return;
        }
        let vivo = true;
        ops.getCampaignResultsSummary(negocio, { from: periodo.from, to: periodo.to, compare: 'previous_period' })
            .then((r) => {
                if (vivo) {
                    setResumen(r);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, veResumen, periodo, intento]);

    if (error !== null) {
        return <EstadoError seccion="los mensajes automáticos" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />;
    }
    if (reglas === null) {
        return <EstadoCargando seccion="los mensajes automáticos" />;
    }
    const hora = horaPorDefecto(franja);
    const vivas = reglas.filter((r) => r.status !== 'archived');
    const lista = vista === 'archivados' ? reglas.filter((r) => r.status === 'archived') : vivas;
    const pausadas = vivas.filter((r) => r.status === 'paused' && r.pause_reason != null && r.pause_reason !== 'manual');
    const recargar = (texto?: string) => {
        setHecho(texto ?? null);
        setIntento((i) => i + 1);
    };

    return (
        <div className="rv-ca-apartado">
            {hecho !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span>{hecho}</span>
                </div>
            )}
            {resumen !== null && periodo !== undefined && (
                <ResultadosAutomaticos
                    resumen={resumen}
                    periodo={periodo}
                    atribucion={atribucion}
                    abierto={verResultados}
                    onAbrir={() => { setVerResultados((a) => !a); }}
                    selector={movil ? <Contexto etiqueta={selectorPeriodo.etiqueta} icono="calendar" valor={selectorPeriodo.valor} opciones={selectorPeriodo.opciones} onChange={selectorPeriodo.onChange} movil /> : null}
                    movil={movil}
                />
            )}
            {pausadas.map((r) => (
                <div key={r.id} className="rv-cn-alerta rv-cn-alerta--amarilla" role="alert">
                    <Icono nombre="pause" />
                    <span>
                        <strong>{`«${r.name}» está pausado`}</strong>
                        <span>{PAUSA_REGLA[r.pause_reason ?? 'manual']}</span>
                    </span>
                </div>
            ))}
            <Filtros etiqueta="Mensajes automáticos" items={vistas.map((v) => ({ etiqueta: v.etiqueta }))} valor={Math.max(0, vistas.findIndex((v) => v.valor === vista))} onCambio={(i) => { setVista(vistas[i]?.valor ?? 'reglas'); }} />
            {vista === 'envios' ? (
                <Envios {...props} regla={null} reglas={reglas} />
            ) : lista.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="zap" />
                    </span>
                    <strong>{vista === 'archivados' ? 'No hay mensajes archivados' : 'Todavía no hay mensajes automáticos'}</strong>
                    <span>Salen solos: después de la visita, al día siguiente, en el cumpleaños o a quien hace tiempo que no viene.</span>
                </div>
            ) : (
                <Tabla
                    etiqueta="Mensajes automáticos"
                    movil={movil}
                    columnas={[
                        { clave: 'nombre', titulo: 'Mensaje', ancho: 'minmax(220px, 1.6fr)' },
                        { clave: 'cuando', titulo: 'Cuándo sale', ancho: 'minmax(180px, 1.2fr)' },
                        { clave: 'res', titulo: 'Resultados', ancho: 'minmax(160px, 1fr)' },
                        { clave: 'estado', titulo: 'Estado', ancho: 'minmax(110px, 0.7fr)' },
                    ]}
                    filas={lista.map((r) => {
                        const e = ESTADO_REGLA[r.status];
                        return {
                            clave: r.id,
                            etiqueta: r.name,
                            onAbrir: () => { setAbierta(r.id); },
                            celdas: {
                                nombre: (
                                    <span className="rv-en-celda">
                                        <strong>{r.name}</strong>
                                        <small>
                                            <Icono nombre={CANAL[r.current_version.channel].icono} /> {`${DISPARADOR[r.trigger].texto} · ${CANAL[r.current_version.channel].nombre}`}
                                        </small>
                                    </span>
                                ),
                                cuando: cuandoSale(r.trigger, r.current_version.trigger_params).replace('a la hora de siempre', `a las ${hora}`),
                                res: r.results === undefined ? '—' : `${cifra(r.results.delivered ?? r.results.recipients ?? 0)} entregados · ${cifra(r.results.conversions ?? 0)} reservas y compras`,
                                estado: e.chip === null ? e.texto : <Chip estado={e.chip}>{e.texto}</Chip>,
                            },
                        };
                    })}
                />
            )}
            {abierta !== null && editar === null && (
                <FichaRegla
                    key={abierta}
                    {...props}
                    reglaId={abierta}
                    reglas={reglas}
                    hora={hora}
                    onCerrar={() => { setAbierta(null); }}
                    onCambio={recargar}
                    onEditar={(r) => { setEditar(r); }}
                />
            )}
            {editar !== null && (
                <EditorRegla
                    {...props}
                    regla={editar === 'nueva' ? null : editar}
                    hora={hora}
                    onCerrar={(texto, id) => {
                        setEditar(null);
                        if (id !== undefined) {
                            setAbierta(id);
                        }
                        recargar(texto);
                    }}
                />
            )}
        </div>
    );
}

// ---- Resultados de los automáticos (12.5) ----

function ResultadosAutomaticos({ resumen, periodo, atribucion, abierto, onAbrir, selector, movil }: { resumen: ResumenResultados; periodo: Periodo; atribucion: number | null; abierto: boolean; onAbrir: () => void; selector: ReactNode; movil: boolean }) {
    // G993: solo los automáticos, ya sumados por Campañas; nunca el total conjunto menos las campañas (G121).
    const t = resumen.automation_totals;
    const p = resumen.previous_automation_totals ?? null;
    const chip = (actual: number | null | undefined, anterior: number | null | undefined, menos = false) => variacion(actual, anterior, menos) ?? undefined;
    const dinero = t.conversions_amount !== undefined && t.conversions_amount.amount > 0 ? t.conversions_amount : null;
    const dias = atribucion === null ? 'En los días siguientes' : `En los ${String(atribucion)} días siguientes`;
    const vacio = t.sent === 0 && t.conversions === 0;
    const fechas = delAl(resumen.from, resumen.to);
    const antes = periodoAnterior(resumen.from, resumen.to);
    const nota = vacio ? 'Sin envíos en este periodo.' : `Todos los automáticos${fechas === null ? '' : ` · ${fechas}`}${antes === null ? '' : ` frente a ${antes}`}`;
    return (
        <>
            <div className="rv-cp-frase">
                <span>
                    {`${periodo.etiqueta}: `}
                    <strong>{cifra(t.conversions)}</strong>
                    {` reservas y compras${dinero === null ? '' : ` (${importe(dinero)})`} después de tus mensajes automáticos.`}
                </span>
                <Boton tipo="secundario" icono={abierto ? 'chevron-up' : 'chevron-down'} movil={movil} aria-expanded={abierto} onClick={onAbrir}>
                    {abierto ? 'Ocultar resultados' : 'Ver resultados'}
                </Boton>
            </div>
            {abierto && (
                <section className="rv-cp-resumen" aria-labelledby="rv-au-res">
                    <div className="rv-cp-resumen__cab">
                        <div className="rv-cp-resumen__titulo">
                            <h2 id="rv-au-res">Resultados</h2>
                            <span>{nota}</span>
                        </div>
                        {selector}
                    </div>
                    <div className="rv-cp-cifras">
                        <Cifra titulo="Reservas y compras" valor={cifra(t.conversions)} pie={dinero === null ? dias : `${importe(dinero)} ${dias.charAt(0).toLocaleLowerCase('es')}${dias.slice(1)}`} oscura cambio={chip(t.conversions, p?.conversions)} />
                        <Cifra titulo="Enviados" valor={cifra(t.sent)} pie="Mensajes automáticos que salieron" cambio={chip(t.sent, p?.sent)} />
                        <Cifra titulo="Entregados" valor={t.delivered === null ? '—' : cifra(t.delivered)} pie={t.sent > 0 && t.delivered !== null ? `${porcentaje(t.delivered, t.sent)} de lo enviado` : 'Nada enviado todavía'} cambio={chip(t.delivered, p?.delivered)} />
                        <Cifra titulo="Bajas" valor={cifra(t.unsubscribes)} pie="Solo el número, nunca quién" cambio={chip(t.unsubscribes, p?.unsubscribes, true)} />
                    </div>
                </section>
            )}
        </>
    );
}

// ---- Ficha de una regla ----

interface PropsFicha extends PropsApartado {
    reglaId: string;
    reglas: Regla[];
    hora: string;
    onCerrar: () => void;
    onCambio: (texto?: string) => void;
    onEditar: (r: Regla) => void;
}

function FichaRegla(props: PropsFicha) {
    const { ops, negocio, movil, soloLectura, reglaId, hora, onCerrar, onCambio, onEditar } = props;
    const { puede } = usePermisos();
    const aviso = useAvisoBreve();
    const [r, setR] = useState<{ datos: Regla; version: string | null } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [pestana, setPestana] = useState(0);
    const [ventana, setVentana] = useState<'activar' | 'archivar' | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [nombresEtiquetas, setNombresEtiquetas] = useState<Readonly<Record<string, string>>>({});

    // G944: «Para quién» dice las etiquetas por su nombre («Con la etiqueta VIP o Habitual»).
    useEffect(() => {
        let vivo = true;
        ops.listTags(negocio, { limit: 100 })
            .then((l) => { if (vivo) setNombresEtiquetas(Object.fromEntries(l.data.map((t) => [t.id, t.name]))); })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio]);

    useEffect(() => {
        let vivo = true;
        ops.getAutomationRule(negocio, reglaId)
            .then((x) => {
                if (vivo) {
                    setR(x);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, reglaId, intento]);

    if (error !== null || r === null) {
        return (
            <PanelLateral abierto onCerrar={onCerrar} movil={movil} tamano="ficha" titulo="Mensaje automático" eyebrow="Mensaje automático">
                {error === null ? <EstadoCargando seccion="el mensaje automático" /> : <EstadoError seccion="el mensaje automático" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setError(null); setIntento((i) => i + 1); }} movil={movil} />}
            </PanelLateral>
        );
    }
    const regla = r.datos;
    const v = regla.current_version;
    const acciones = accionesRegla(regla, { puede, soloLectura });
    const e = ESTADO_REGLA[regla.status];
    const pestanas = [
        { etiqueta: 'Mensaje', icono: 'zap' as const },
        { etiqueta: 'Resultados', icono: 'chart-column' as const },
        ...(puede('campanas.destinatarios.ver') ? [{ etiqueta: 'Envíos', icono: 'list-checks' as const }] : []),
        { etiqueta: 'Versiones', icono: 'history' as const },
    ];
    const indice = Math.min(pestana, pestanas.length - 1);
    const nombrePestana = pestanas[indice]?.etiqueta;
    const hacer = (accion: () => Promise<unknown>, texto: string) => {
        setOcupado(true);
        accion()
            .then(() => {
                setVentana(null);
                setIntento((i) => i + 1);
                onCambio(texto);
            })
            .catch((x: unknown) => {
                if (x instanceof ErrorApi && x.estado === 412) {
                    aviso({ tipo: 'error', texto: 'Alguien lo ha cambiado: lo volvemos a leer.' });
                    setIntento((i) => i + 1);
                    return;
                }
                aviso({ tipo: 'error', texto: avisoBreveDeError(x) ?? (x instanceof Error ? x.message : 'No se ha podido hacer.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };
    const pulsar = (a: AccionRegla) => {
        if (a === 'editar') {
            onEditar(regla);
        } else if (a === 'activar') {
            setVentana('activar');
        } else if (a === 'archivar') {
            setVentana('archivar');
        } else {
            hacer(() => ops.pauseAutomationRule(negocio, regla.id), `«${regla.name}» pausado: deja de programar y anula lo pendiente.`);
        }
    };
    const TEXTO: Readonly<Record<AccionRegla, { texto: string; icono: 'pencil' | 'play' | 'pause' | 'archive' }>> = {
        editar: { texto: 'Cambiar', icono: 'pencil' },
        activar: { texto: 'Activar', icono: 'play' },
        pausar: { texto: 'Pausar', icono: 'pause' },
        archivar: { texto: 'Archivar', icono: 'archive' },
    };

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                titulo={regla.name}
                eyebrow="Mensaje automático"
                subtitulo={`${DISPARADOR[regla.trigger].texto} · versión ${String(v.number)}`}
                {...(e.chip === null ? {} : { chip: e.texto, chipEstado: e.chip })}
                icono={DISPARADOR[regla.trigger].icono}
            >
                <div className="rv-cn-ficha">
                    <Segmentos etiqueta="Partes del mensaje automático" movil={movil} items={pestanas} valor={indice} onCambio={setPestana} />
                    {nombrePestana === 'Mensaje' && (
                        <>
                            {regla.status === 'paused' && regla.pause_reason != null && (
                                <div className="rv-cn-alerta rv-cn-alerta--amarilla" role="alert">
                                    <Icono nombre="pause" />
                                    <span>{PAUSA_REGLA[regla.pause_reason]}</span>
                                </div>
                            )}
                            {acciones.length > 0 && (
                                <div className="rv-cp-franja__botones">
                                    {acciones.map((a) => (
                                        <Boton key={a} tipo={a === 'activar' ? 'principal' : 'secundario'} rojo={a === 'archivar'} icono={TEXTO[a].icono} movil={movil} onClick={() => { pulsar(a); }}>
                                            {TEXTO[a].texto}
                                        </Boton>
                                    ))}
                                </div>
                            )}
                            <div className="rv-cn-hechos">
                                <Hecho k="Cuándo sale" v={cuandoSale(regla.trigger, v.trigger_params).replace('a la hora de siempre', `a la hora de siempre: las ${hora}`)} />
                                <Hecho k="Canal" v={CANAL[v.channel].nombre} />
                                <Hecho k="Para quién" v={textoParaQuien(v.segment?.tag_ids_any ?? [], nombresEtiquetas)} />
                                {regla.activated_at != null && <Hecho k="Activo desde" v={diaMes(regla.activated_at)} />}
                            </div>
                            <div className="rv-cp-seccion">
                                <h3>Así le llega</h3>
                                <div className="rv-cp-muestra">{v.content.text ?? v.content.email_body_text ?? v.content.email_subject ?? 'Con su plantilla de WhatsApp.'}</div>
                            </div>
                        </>
                    )}
                    {nombrePestana === 'Resultados' && <ResultadosRegla {...props} regla={regla} />}
                    {nombrePestana === 'Envíos' && <Envios {...props} regla={regla} reglas={[regla]} />}
                    {nombrePestana === 'Versiones' && <Versiones {...props} regla={regla} />}
                </div>
            </PanelLateral>
            <Ventana
                abierta={ventana !== null}
                titulo={ventana === 'activar' ? `Activar «${regla.name}»` : `Archivar «${regla.name}»`}
                texto={ventana === 'activar' ? `Desde ahora sale solo: ${cuandoSale(regla.trigger, v.trigger_params).toLocaleLowerCase('es')}, por ${CANAL[v.channel].nombre}, a quien tenga permiso.` : regla.status === 'draft' ? 'Este borrador no ha salido nunca. Se archiva y deja de verse en Mensajes.' : 'Deja de salir y se anula lo programado. Sus resultados se quedan en Archivados.'}
                accion={ventana === 'activar' ? 'Activar' : 'Archivar'}
                accionIcono={ventana === 'activar' ? 'play' : 'archive'}
                cancelar="Volver"
                ocupado={ocupado}
                ocupadoTexto={ventana === 'activar' ? 'Activando…' : 'Archivando…'}
                movil={movil}
                onAccion={() => {
                    if (ventana === 'activar') {
                        hacer(() => ops.activateAutomationRule(negocio, regla.id, r.version ?? ''), `«${regla.name}» activo.`);
                    } else {
                        hacer(() => ops.archiveAutomationRule(negocio, regla.id), `«${regla.name}» archivado.`);
                    }
                }}
                onCerrar={() => { setVentana(null); }}
            />
        </>
    );
}

function Hecho({ k, v }: { k: string; v: string }) {
    return (
        <div className="rv-cn-hecho">
            <span>{k}</span>
            <strong>{v}</strong>
        </div>
    );
}

function ResultadosRegla({ ops, negocio, movil, regla }: PropsApartado & { regla: Regla }) {
    const { puede } = usePermisos();
    const completos = puede('campanas.resultados.ver');
    const [r, setR] = useState<Resultados | null>(null);
    const [error, setError] = useState<unknown>(null);
    useEffect(() => {
        if (!completos) {
            return;
        }
        let vivo = true;
        ops.getAutomationRuleResults(negocio, regla.id)
            .then((x) => {
                if (vivo) {
                    setR(x);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, regla.id, completos]);
    if (error !== null) {
        return <EstadoError seccion="los resultados" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setError(null); }} movil={movil} />;
    }
    if (completos && r === null) {
        return <EstadoCargando seccion="los resultados" />;
    }
    const h = regla.results ?? {};
    const omitidos = Object.entries(r?.skipped_by_reason ?? {}).filter(([, n]) => n > 0);
    return (
        <div className="rv-cn-ficha">
            <div className="rv-cp-cifras">
                <Cifra titulo="Reservas y compras" valor={cifra(r?.conversions ?? h.conversions ?? 0)} pie="En los días siguientes" oscura />
                <Cifra titulo="Enviados" valor={cifra(r?.sent ?? h.recipients ?? 0)} />
                <Cifra titulo="Entregados" valor={(r === null ? h.delivered : r.delivered) == null ? '—' : cifra((r === null ? h.delivered : r.delivered) ?? 0)} />
                <Cifra titulo="Bajas" valor={cifra(r?.unsubscribes ?? h.unsubscribes ?? 0)} pie="Solo el número, nunca quién" />
            </div>
            {r !== null && motivosDe(r).length + omitidos.length > 0 && (
                <div className="rv-cp-seccion">
                    <h3>Por qué no salió</h3>
                    <ul className="rv-cp-lista">
                        {motivosDe(r).map((m) => (
                            <li key={m.clave}>
                                <span>{m.texto}</span>
                                <strong>{cifra(m.cifra)}</strong>
                            </li>
                        ))}
                        {omitidos.map(([k, n]) => (
                            <li key={`o-${k}`}>
                                <span>{(OMITIDO as Readonly<Record<string, string>>)[k] ?? k}</span>
                                <strong>{cifra(n)}</strong>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {!completos && <p className="rv-ca-nota"><Icono nombre="eye" /><span>El detalle por cliente solo lo ven el propietario y los administradores.</span></p>}
        </div>
    );
}

function Envios({ ops, negocio, movil, regla, reglas }: PropsApartado & { regla: Regla | null; reglas: Regla[] }) {
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<{ data: EnvioAutomatico[]; next_cursor: string | null } | null>(null);
    const [error, setError] = useState<unknown>(null);
    useEffect(() => {
        let vivo = true;
        ops.listAutomatedSends(negocio, { ...(regla === null ? {} : { rule_id: regla.id }), ...(pag.cursor === null ? {} : { cursor: pag.cursor }), limit: 20 })
            .then((x) => {
                if (vivo) {
                    setLista(x);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, regla, pag.cursor]);
    if (error !== null) {
        return <EstadoError seccion="los envíos" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setError(null); pag.reiniciar(); }} movil={movil} />;
    }
    if (lista === null) {
        return <EstadoCargando seccion="los envíos" />;
    }
    const nombre = (id: string) => reglas.find((x) => x.id === id)?.name ?? 'Mensaje automático';
    return (
        <div className="rv-cp-seccion">
            <p>Lo programado, lo enviado y lo que no salió, con su motivo. Quién se da de baja no se ve: solo cuántos, en los resultados.</p>
            <ul className="rv-cp-lista" aria-label="Envíos">
                {lista.data.length === 0 && <li>Todavía no hay envíos.</li>}
                {lista.data.map((e) => {
                    const m = motivoEnvio(e);
                    const est = ESTADO_ENVIO_AUTOMATICO[m.estado];
                    return (
                        <li key={e.id}>
                            <span>
                                <strong>{regla === null ? nombre(e.rule_id) : diaYHora(e.due_at)}</strong>
                                <small>{m.motivo ?? (regla === null ? diaYHora(e.due_at) : est.texto)}</small>
                            </span>
                            {est.chip === null ? <span>{est.texto}</span> : <Chip estado={est.chip}>{est.texto}</Chip>}
                        </li>
                    );
                })}
            </ul>
            <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={lista.next_cursor !== null} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
        </div>
    );
}

function Versiones({ ops, negocio, regla }: PropsApartado & { regla: Regla }) {
    const [lista, setLista] = useState<VersionRegla[] | null>(null);
    useEffect(() => {
        let vivo = true;
        ops.listAutomationRuleVersions(negocio, regla.id, { limit: 50 })
            .then((x) => {
                if (vivo) {
                    setLista(x.data);
                }
            })
            .catch(() => {
                if (vivo) {
                    setLista([]);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, regla.id]);
    if (lista === null) {
        return <EstadoCargando seccion="las versiones" />;
    }
    return (
        <div className="rv-cp-seccion">
            <p>Cambiarlo crea una versión nueva; lo ya programado sale con la suya.</p>
            <ul className="rv-cp-lista">
                {lista.map((x) => (
                    <li key={x.id}>
                        <span>
                            <strong>{`Versión ${String(x.number)}${x.number === regla.current_version.number ? ' · la que vale' : ''}`}</strong>
                            <small>{`${cuandoSale(regla.trigger, x.trigger_params)} · ${CANAL[x.channel].nombre} · ${diaMes(x.created_at)}`}</small>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ---- Crear o cambiar una regla ----

interface PropsEditor extends PropsApartado {
    regla: Regla | null;
    hora: string;
    onCerrar: (texto?: string, id?: string) => void;
}

const DISPARADORES: readonly Disparador[] = ['post_visit', 'next_day', 'first_visit', 'birthday', 'inactive'];
const ORIGENES = [
    { valor: 'reserva', texto: 'Reservas', explica: 'Al completarse la reserva.' },
    { valor: 'cita', texto: 'Citas', explica: 'Al completarse la cita.' },
    { valor: 'evento', texto: 'Eventos', explica: 'Al registrarse la llegada.' },
] as const;

function EditorRegla({ ops, negocio, movil, navegar, regla, hora, onCerrar }: PropsEditor) {
    const aviso = useAvisoBreve();
    const v = regla?.current_version;
    const [nombre, setNombre] = useState(regla?.name ?? '');
    const [disparador, setDisparador] = useState<Disparador>(regla?.trigger ?? 'post_visit');
    const [horas, setHoras] = useState(String(Math.round((v?.trigger_params.delay_minutes ?? 120) / 60)));
    const [conHora, setConHora] = useState(v?.trigger_params.send_local_time !== undefined);
    const [horaPropia, setHoraPropia] = useState(v?.trigger_params.send_local_time ?? '12:00');
    const [dias, setDias] = useState(String(v?.trigger_params.inactive_days ?? 90));
    const [origenes, setOrigenes] = useState<string[]>(v?.visit_sources ?? ['reserva', 'cita', 'evento']);
    const [canal, setCanal] = useState<CanalCampana>(v?.channel ?? 'whatsapp');
    const [plantilla, setPlantilla] = useState(v?.content.meta_template_id ?? '');
    const [variables, setVariables] = useState<Record<string, Variable>>({ ...(v?.content.template_variables ?? {}) });
    const [texto, setTexto] = useState(v?.content.text ?? v?.content.email_body_text ?? '');
    const [asunto, setAsunto] = useState(v?.content.email_subject ?? '');
    const [plantillas, setPlantillas] = useState<PlantillaMarketing[]>([]);
    const [ocupado, setOcupado] = useState(false);
    const [intentado, setIntentado] = useState(false);
    const [vista, setVista] = useState<{ id: string; previa: VistaPreviaRegla } | null>(null);
    // «Para quién» (12.5): todos o con alguna etiqueta, y «Vino a» (`service_ids_any`, `event_ids_any`).
    const [para, setPara] = useState<ParaQuien>(() => paraQuienDe(v?.segment));
    const [etiquetas, setEtiquetas] = useState<{ id: string; nombre: string }[]>([]);
    const [vinoA, setVinoA] = useState<{ servicios: { id: string; nombre: string }[]; eventos: { id: string; nombre: string }[] }>({ servicios: [], eventos: [] });

    useEffect(() => {
        let vivo = true;
        ops.listMarketingTemplates(negocio, { status: 'approved', limit: 100 })
            .then((r) => {
                if (vivo) {
                    setPlantillas(r.data);
                }
            })
            .catch(() => undefined);
        ops.listTags(negocio, { limit: 100 })
            .then((r) => {
                if (vivo) {
                    setEtiquetas(r.data.map((t) => ({ id: t.id, nombre: t.name })));
                }
            })
            .catch(() => undefined);
        // Como en las campañas: los servicios activos y los eventos publicados o ya pasados; sin permiso o sin el bloque, sin ellos.
        void Promise.all([
            ops.listServices(negocio, { status: 'active', limit: 100 }).catch(() => null),
            ops.listEvents(negocio, { status: ['published', 'ended'], limit: 100 }).catch(() => null),
        ]).then(([servicios, eventos]) => {
            if (vivo) {
                setVinoA({
                    servicios: (servicios?.data ?? []).map((x) => ({ id: x.id, nombre: x.current_version.name })),
                    eventos: (eventos?.data ?? []).map((e) => ({ id: e.id, nombre: `${e.name ?? ''} · ${e.local_start_date.split('-').reverse().join('/')}` })),
                });
            }
        });
        return () => {
            vivo = false;
        };
    }, [ops, negocio]);

    const conHoraPosible = disparador === 'next_day' || disparador === 'birthday';
    const errores: Record<string, string> = {};
    if (nombre.trim() === '') {
        errores.nombre = 'Ponle un nombre.';
    }
    if ((disparador === 'post_visit' || disparador === 'first_visit') && !(Number(horas) >= 1 && Number(horas) <= 336)) {
        errores.horas = 'De 1 hora a 14 días (336 horas).';
    }
    if (disparador === 'inactive' && !(Number(dias) >= 14 && Number(dias) <= 730)) {
        errores.dias = 'De 14 a 730 días.';
    }
    if (canal === 'whatsapp' && plantilla === '') {
        errores.mensaje = 'Elige una plantilla aprobada.';
    } else if (canal !== 'whatsapp' && texto.trim() === '') {
        errores.mensaje = 'Escribe el mensaje.';
    }
    if (canal === 'email' && asunto.trim() === '') {
        errores.asunto = 'Escribe el asunto.';
    }
    if (para.modo === 'tags' && para.etiquetas.length === 0) {
        errores.etiquetas = 'Elige al menos una etiqueta.';
    }

    const guardar = () => {
        setIntentado(true);
        if (Object.keys(errores).length > 0) {
            return;
        }
        const params = disparador === 'post_visit' || disparador === 'first_visit' ? { delay_minutes: Number(horas) * 60 } : disparador === 'inactive' ? { inactive_days: Number(dias) } : conHora ? { send_local_time: horaPropia } : {};
        const content: ContenidoCampana = canal === 'whatsapp' ? { kind: 'meta_template', meta_template_id: plantilla, template_variables: variables } : canal === 'telegram' ? { kind: 'text', text: texto } : { kind: 'email', email_subject: asunto, email_body_text: texto };
        const fuentes = disparador === 'birthday' || disparador === 'inactive' ? {} : { visit_sources: origenes as ('reserva' | 'cita' | 'evento')[] };
        const segment: Segmento = segmentoDeRegla(v?.segment, para);
        setOcupado(true);
        (async () => {
            const guardada =
                regla === null
                    ? await ops.createAutomationRule(negocio, { name: nombre.trim(), trigger: disparador, trigger_params: params, segment, channel: canal, content, ...fuentes })
                    : await ops.updateAutomationRule(negocio, regla.id, { name: nombre.trim(), trigger_params: params, segment, channel: canal, content, ...fuentes }, (await ops.getAutomationRule(negocio, regla.id)).version ?? '');
            const previa = await ops.previewAutomationRule(negocio, guardada.datos.id).catch(() => null);
            if (previa === null) {
                onCerrar(regla === null ? 'Borrador guardado.' : 'Versión nueva guardada.', guardada.datos.id);
                return;
            }
            setVista({ id: guardada.datos.id, previa });
        })()
            .catch((e: unknown) => {
                aviso({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof Error ? e.message : 'No se ha podido guardar.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const plantillaElegida = plantillas.find((p) => p.template_id === plantilla);
    return (
        <PanelLateral
            abierto
            onCerrar={() => { onCerrar(vista === null ? undefined : regla === null ? 'Borrador guardado.' : 'Versión nueva guardada.', vista?.id); }}
            movil={movil}
            titulo={regla === null ? (nombre.trim() === '' ? 'Nuevo mensaje automático' : nombre.trim()) : regla.name}
            eyebrow={regla === null ? 'Nuevo mensaje automático' : `Versión ${String((v?.number ?? 0) + 1)} · ${regla.name}`}
            subtitulo={regla === null ? 'Se guarda como borrador.' : `Ahora vale la versión ${String(v?.number ?? 1)}.`}
            icono={regla === null ? 'plus' : 'pencil'}
            accion={vista !== null ? 'Hecho' : regla === null ? 'Guardar borrador' : `Guardar versión ${String((v?.number ?? 0) + 1)}`}
            ocupado={ocupado}
            onAccion={vista !== null ? () => { onCerrar(regla === null ? 'Borrador guardado.' : 'Versión nueva guardada.', vista.id); } : guardar}
        >
            {vista !== null ? (
                <div className="rv-cp-form">
                    <div className="rv-cp-cifras">
                        <Cifra titulo="Con permiso hoy" valor={cifra(vista.previa.eligible_now)} pie="Clientes del segmento con permiso en el canal" oscura />
                        <Cifra titulo="Fuera por no tener permiso" valor={cifra(vista.previa.excluded_no_consent)} />
                        {vista.previa.with_birth_date != null && <Cifra titulo="Con fecha de nacimiento" valor={cifra(vista.previa.with_birth_date)} pie="Solo a ellos les llegará" />}
                    </div>
                    {vista.previa.sample !== undefined && (
                        <div className="rv-cp-muestra">
                            {vista.previa.sample.subject != null && <strong>{vista.previa.sample.subject}</strong>}
                            {vista.previa.sample.body !== undefined && <span>{vista.previa.sample.body}</span>}
                            {vista.previa.sample.unsubscribe_footer !== undefined && <small>{vista.previa.sample.unsubscribe_footer}</small>}
                        </div>
                    )}
                </div>
            ) : (
                <div className="rv-cp-form">
                    <Campo etiqueta="Nombre" valor={nombre} onChange={setNombre} error={intentado ? (errores.nombre ?? null) : null} />
                    {regla === null ? (
                        DISPARADORES.map((d) => <OpcionRadio key={d} elegida={disparador === d} onElegir={() => { setDisparador(d); }} icono={DISPARADOR[d].icono} titulo={DISPARADOR[d].texto} texto={DISPARADOR[d].explica} />)
                    ) : (
                        <p className="rv-ca-nota">
                            <Icono nombre={DISPARADOR[disparador].icono} />
                            <span>{`${DISPARADOR[disparador].texto}. El disparador no se cambia después.`}</span>
                        </p>
                    )}
                    {(disparador === 'post_visit' || disparador === 'first_visit') && <Campo etiqueta="Horas después de la visita" tipo="number" modo="numeric" valor={horas} onChange={setHoras} error={intentado ? (errores.horas ?? null) : null} />}
                    {disparador === 'inactive' && <Campo etiqueta="Días sin venir" tipo="number" modo="numeric" valor={dias} onChange={setDias} error={intentado ? (errores.dias ?? null) : null} ayuda={`Sale a la hora de siempre: las ${hora}.`} />}
                    {conHoraPosible && (
                        <>
                            <Interruptor etiqueta="Elegir la hora" valor={conHora} onChange={setConHora} {...(conHora ? {} : { nota: `Sale a la hora de siempre: las ${hora}` })} />
                            {conHora && <Desplegable etiqueta="Hora" valor={horaPropia} opciones={HORAS.map((h) => ({ valor: h, texto: h }))} onChange={setHoraPropia} />}
                        </>
                    )}
                    {disparador !== 'birthday' && disparador !== 'inactive' && (
                        <div className="rv-cp-seccion">
                            <h3>Visitas que cuentan</h3>
                            {ORIGENES.map((o) => (
                                <Casilla key={o.valor} marcada={origenes.includes(o.valor)} onCambio={(m) => { setOrigenes(m ? [...origenes, o.valor] : origenes.filter((x) => x !== o.valor)); }}>
                                    {`${o.texto} · ${o.explica}`}
                                </Casilla>
                            ))}
                        </div>
                    )}
                    <div className="rv-cp-seccion">
                        <h3>Para quién</h3>
                        <div role="radiogroup" aria-label="Para quién" className="rv-cp-seccion">
                            <OpcionRadio elegida={para.modo === 'all'} onElegir={() => { setPara({ ...para, modo: 'all' }); }} titulo="Todos los clientes" texto="Con permiso comercial en el canal" />
                            <OpcionRadio elegida={para.modo === 'tags'} onElegir={() => { setPara({ ...para, modo: 'tags' }); }} titulo="Solo con alguna de estas etiquetas" texto="De Clientes · Etiquetas" />
                        </div>
                        {para.modo === 'tags' && (
                            <div className="rv-cp-pastillas" role="group" aria-label="Etiquetas">
                                {etiquetas.map((t) => {
                                    const on = para.etiquetas.includes(t.id);
                                    return (
                                        <button key={t.id} type="button" className={`rv-cp-pastilla${on ? ' rv-cp-pastilla--on' : ''}`} aria-pressed={on} onClick={() => { setPara({ ...para, etiquetas: on ? para.etiquetas.filter((x) => x !== t.id) : [...para.etiquetas, t.id] }); }}>
                                            <Icono nombre={on ? 'check' : 'tag'} />
                                            {t.nombre}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {para.modo === 'tags' && intentado && errores.etiquetas !== undefined && <p className="rv-cp-error" role="alert">{errores.etiquetas}</p>}
                        {/* «Vino a» (G940, G941): evento y servicio, como en el diseño; un valor en cada uno. */}
                        {vinoA.eventos.length > 0 && (
                            <Desplegable etiqueta="Vino a (evento)" valor={para.evento} opciones={[{ valor: '', texto: 'Cualquier evento' }, ...vinoA.eventos.map((o) => ({ valor: o.id, texto: o.nombre }))]} onChange={(x) => { setPara({ ...para, evento: x }); }} ayuda="Opcional: solo quien vino a eso." />
                        )}
                        {vinoA.servicios.length > 0 && (
                            <Desplegable etiqueta="Vino a (servicio)" valor={para.servicio} opciones={[{ valor: '', texto: 'Cualquier servicio' }, ...vinoA.servicios.map((o) => ({ valor: o.id, texto: o.nombre }))]} onChange={(x) => { setPara({ ...para, servicio: x }); }} ayuda="Opcional: solo quien vino a eso." />
                        )}
                        <p>Siempre solo a quien aceptó recibir información comercial por ese canal. Los importados sin prueba, nunca.</p>
                    </div>
                    <Desplegable etiqueta="Canal" valor={canal} opciones={(Object.keys(CANAL) as CanalCampana[]).map((k) => ({ valor: k, texto: CANAL[k].nombre }))} onChange={(x) => { setCanal(x as CanalCampana); }} />
                    {canal === 'whatsapp' ? (
                        <>
                            <Desplegable
                                etiqueta="Plantilla"
                                valor={plantilla}
                                opciones={[{ valor: '', texto: plantillas.length === 0 ? 'No hay plantillas aprobadas' : 'Elige una plantilla' }, ...plantillas.map((p) => ({ valor: p.template_id, texto: p.name }))]}
                                onChange={(x) => { setPlantilla(x); setVariables(variablesDe(plantillas.find((p) => p.template_id === x)?.body ?? '')); }}
                                error={intentado ? (errores.mensaje ?? null) : null}
                            />
                            <button type="button" className="rv-link" onClick={() => { navegar(RUTA_PLANTILLAS_META); }}>
                                Nueva plantilla
                            </button>
                            {plantillaElegida?.body != null && <div className="rv-cp-muestra">{plantillaElegida.body}</div>}
                        </>
                    ) : (
                        <>
                            {canal === 'email' && <Campo etiqueta="Asunto" valor={asunto} onChange={setAsunto} error={intentado ? (errores.asunto ?? null) : null} />}
                            <Campo etiqueta="Mensaje" valor={texto} onChange={setTexto} filas={6} error={intentado ? (errores.mensaje ?? null) : null} />
                        </>
                    )}
                </div>
            )}
        </PanelLateral>
    );
}
